import React, { useState, useEffect, useMemo } from 'react';
import map from 'lodash/map';
import get from 'lodash/get';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useHistory } from 'react-router-dom';

import { saveToCSV, CSV_MODAL_TYPES } from 'utils/admin-csv';

import usePagination from 'hooks/usePagination';
import {
  getProductsCatalog,
  downloadProductsCSV,
} from 'services/api/admin-products';

import PageContainer from 'shared-components/PageContainer/PageContainer';
import AdminTable from 'shared-components/AdminTable/AdminTable';
import AdminSearchBar from 'shared-components/AdminSearchBar/AdminSearchBar';

import Button, { BUTTON_TYPES } from 'shared-components/Button/Button';
import searchStyles from 'shared-components/AdminSearchBar/AdminSearchBar.module.css';
import { Toast } from 'shared-components/BaseToast/Toast';
import AdminOptionsButton from 'shared-components/AdminOptionsButton/AdminOptionsButton';
import Pagination from 'shared-components/Pagination/Pagination';
import ProductCards from './ProductCards/ProductCards';
import ProductsPerPage from './ProductsPerPage/ProductsPerPage';
import TotalProducts from './TotalProducts/TotalProducts';
import { ReactComponent as FilterSVG } from './images/filter.svg';
import ProductCatalogModal from './ProductCatalogModal/ProductCatalogModal';
import DownloadNotification from './DownloadNotification/DownloadNotification';

import productModel from './productModel';
import productsInBulkModel from './productsInBulkModel';

import styles from './ProductCatalog.module.css';

const PRODUCTS_PER_PAGE = [
  {
    value: 24,
    label: '24',
  },
  {
    value: 48,
    label: '48',
  },
  {
    value: 100,
    label: '100',
  },
  {
    value: 200,
    label: '200',
  },
  {
    value: null,
    label: 'View All',
    width: 68,
  },
];

const formatProducts = (products) => {
  return map(products, (product) => ({
    ...product,
    imageUrl: product.defaultVariant && product.defaultVariant.imageUrl,
    retailPrice:
      product.defaultVariant &&
      (product.defaultVariant.retailPrice / 100).toFixed(2),
    sku: product.defaultVariant.sku,
  }));
};

const formatProductsInBulk = (products) => {
  return map(products, (product) => ({
    ...product,
    sku: get(product, ['defaultVariant', 'sku']),
    parentPostId: get(product, 'legacyPostId'),
  }));
};

const ProductCatalog = () => {
  const [catalog, setCatalog] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [tableView, setTableView] = useState(true);
  const [search, setSearch] = useState('');
  const [isPublic, setIsPublic] = useState(null);
  const [baseModalOpen, setBaseModalOpen] = useState(null);
  const [productsCreatedInBulk, setProductsCreatedInBulk] = useState(null);
  const { disableProductCatalogSave: disableProductCatalogSaveFF } = useFlags();

  const {
    limit,
    offset,
    currentPage,
    onClickLimit,
    onClickPage,
    resetPagination,
  } = usePagination({ initialLimit: 24 });

  const history = useHistory();

  const filterOptions = [
    {
      id: 1,
      name: 'All',
      value: null,
      onClick: () => setIsPublic(null),
    },
    {
      id: 2,
      name: 'Private',
      value: false,
      onClick: () => setIsPublic(false),
    },
    {
      id: 3,
      name: 'Public',
      value: true,
      onClick: () => setIsPublic(true),
    },
  ];
  const newProductOptions = [
    {
      id: 1,
      name: 'Create New Product',
      icon: 'far fa-plus',
      onClick: () => history.push('/admin/product-catalog/create'),
    },
    {
      id: 2,
      name: 'Bulk Upload Products',
      icon: 'far fa-arrow-circle-up',
      onClick: () => setBaseModalOpen(CSV_MODAL_TYPES.addProducts),
    },
  ];

  const { products, totalProductCount, filteredProductCount } = catalog;
  const formattedProducts = useMemo(() => formatProducts(products), [catalog]);
  const pageCount = limit ? Math.ceil(filteredProductCount / limit) : 1;

  const toggleView = () => setTableView((prevState) => !prevState);
  const handleClickDownload = () => {
    window.location.href = downloadProductsCSV(isPublic, search);
  };
  const handleSubmitSearch = (searchQuery) => {
    resetPagination();
    setSearch(searchQuery);
  };
  const handleClickEdit = (legacyPostId) =>
    history.push(`/admin/product-catalog/edit/${legacyPostId}`);
  // download newly created products in bulk
  const handleDownloadCSV = () => {
    const productsCSV = formatProductsInBulk(productsCreatedInBulk);
    saveToCSV(productsInBulkModel, productsCSV, 'products-created');
    setProductsCreatedInBulk(null);
  };

  const getProducts = async () => {
    try {
      setIsLoading(true);
      const response = await getProductsCatalog(
        limit,
        offset,
        isPublic,
        search
      );
      setCatalog(response);
      setIsLoading(false);
    } catch (err) {
      console.error(err);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getProducts();
  }, [limit, offset, isPublic, search]);

  return (
    <PageContainer>
      {!!productsCreatedInBulk && (
        <DownloadNotification
          onDismiss={() => setProductsCreatedInBulk(null)}
          onAccept={handleDownloadCSV}
        />
      )}
      <div className={styles.container}>
        <>
          <div className={styles.header}>
            <h1>Product Catalog</h1>
            <AdminOptionsButton
              buttonType={BUTTON_TYPES.TERTIARY}
              options={newProductOptions}
              disabled={disableProductCatalogSaveFF}
              menuStyle={{ width: '100%' }}
            >
              Create a Product
            </AdminOptionsButton>
          </div>
          <div className={styles.topBar}>
            <div className={styles.leftPanel}>
              <TotalProducts
                search={search}
                isPublic={isPublic}
                filteredProductCount={filteredProductCount}
                totalProductCount={totalProductCount}
                isLoading={isLoading}
              />
              <AdminSearchBar
                disabled={isLoading}
                styles={searchStyles}
                placeholder="Search by product name, brand name, sku, or product id"
                onSubmit={handleSubmitSearch}
              />
            </div>
            <div className={styles.rightPanel}>
              <AdminOptionsButton
                buttonType={
                  isPublic === null
                    ? BUTTON_TYPES.PRIMARY_INVERSE
                    : BUTTON_TYPES.PRIMARY
                }
                options={filterOptions}
                selectedOption={isPublic}
                disabled={isLoading}
                buttonClass={styles.iconButton}
                menuStyle={{ width: 120 }}
              >
                <i className="far fa-sliders-h" />
              </AdminOptionsButton>
              <Button
                buttonType={BUTTON_TYPES.PRIMARY_INVERSE}
                disabled={isLoading}
                className={styles.iconButton}
                onClick={toggleView}
              >
                {tableView ? <FilterSVG /> : <i className="far fa-list" />}
              </Button>
              <Button
                buttonType={BUTTON_TYPES.PRIMARY_INVERSE}
                disabled={isLoading || !filteredProductCount}
                className={styles.iconButton}
                onClick={handleClickDownload}
              >
                <i className="far fa-arrow-to-bottom" />
              </Button>
              <Button
                buttonType="primary"
                disabled={disableProductCatalogSaveFF || isLoading}
                style={{ width: 130 }}
                onClick={() => setBaseModalOpen(CSV_MODAL_TYPES.editProducts)}
              >
                CSV Edit
              </Button>
              <ProductsPerPage
                buttons={PRODUCTS_PER_PAGE}
                limit={limit}
                onClick={onClickLimit}
              />
            </div>
          </div>
          <div className={styles.productView}>
            {tableView ? (
              <AdminTable
                model={productModel}
                rows={formattedProducts}
                isLoading={isLoading}
                idFieldName="legacyPostId"
                imagesFieldName="imageUrl"
                onClickEdit={handleClickEdit}
                emptyIconClassName="fal fa-ghost dark"
                emptyTitle="No Products Match Your Search"
                emptyDescription="Please clear your search and try again."
                hasActions
              />
            ) : (
              <ProductCards
                products={formattedProducts}
                isLoading={isLoading}
              />
            )}
            {products && pageCount > 1 && (
              <Pagination
                pageCount={pageCount}
                currentPage={currentPage}
                containerClassName={styles.paginationContainer}
                onPageChange={onClickPage}
              />
            )}
          </div>
        </>
      </div>
      <ProductCatalogModal
        getProducts={getProducts}
        baseModalOpen={baseModalOpen}
        setBaseModalOpen={setBaseModalOpen}
        setProductsCreatedInBulk={setProductsCreatedInBulk}
      />
      <Toast />
    </PageContainer>
  );
};

export default ProductCatalog;
